import { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAdmin } from '../context/AdminContext';
import { fetchAddressByCep } from '../utils/viacep';
import { geocodeAddress, calculateDistance } from '../utils/distance';
import { mascaraCpfCnpj, validarCpfCnpj } from '../utils/documentValidator';
import {
  MapPin, ArrowRight, CheckCircle, Package, User,
  PlusCircle, AlertTriangle, ArrowLeft, RotateCcw, Zap,
  Phone, Building, Bike, Sparkles
} from 'lucide-react';

/* ─── Types ─── */
interface Address {
  cep: string;
  logradouro: string;
  numero: string;
  complemento: string;
  bairro: string;
  cidade: string;
  lat?: number;
  lon?: number;
}

const emptyAddress: Address = {
  cep: '', logradouro: '', numero: '', complemento: '', bairro: '', cidade: ''
};

const stepLabels = ['Contato', 'Rota', 'Item', 'Recebedor'];

/* ─── Page Transition Variants ─── */
const pageVariants = {
  enter: { opacity: 0, x: 40, filter: 'blur(4px)' },
  center: { opacity: 1, x: 0, filter: 'blur(0px)' },
  exit: { opacity: 0, x: -40, filter: 'blur(4px)' },
};

/* ─── Reusable Input ─── */
function FormInput({
  label, value, onChange, placeholder, type = 'text', className = '', id, isHighlighted, required
}: {
  label: string; value: string; onChange: (v: string) => void;
  placeholder?: string; type?: string; className?: string; id?: string;
  isHighlighted?: boolean; required?: boolean;
}) {
  return (
    <div className={className}>
      <label className={`block text-[10px] sm:text-xs font-semibold uppercase tracking-wider mb-1.5 ${
        isHighlighted ? 'text-amber-600' : 'text-slate-500'
      }`}>
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full rounded-xl border px-2.5 py-3 sm:p-3 text-sm outline-none transition-all duration-200 ${
          isHighlighted
            ? 'border-amber-400/80 bg-amber-50/10 text-slate-800 placeholder:text-amber-400 focus:border-amber-500 focus:ring-2 focus:ring-amber-500/15 hover:border-amber-500'
            : 'border-slate-200/80 bg-white/60 text-slate-800 placeholder:text-slate-400 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 hover:border-slate-300'
        }`}
      />
    </div>
  );
}

/* ─── Address Block ─── */
function AddressBlock({
  title, icon, address, setAddress, onCepLookup, numberInputId
}: {
  title: string;
  icon: React.ReactNode;
  address: Address;
  setAddress: (a: Address) => void;
  onCepLookup: (cep: string, setter: (a: Address) => void, addr: Address) => void;
  numberInputId?: string;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-gradient-to-br from-white/60 to-slate-50/40 p-3 rounded-xl border border-slate-100/80 mb-3 shadow-sm hover:shadow-md transition-shadow duration-300"
    >
      <h3 className="font-bold text-slate-800 mb-3 flex items-center gap-2 text-sm">
        {icon}
        {title}
      </h3>
      {/* Linha 1: CEP | Endereço | Número */}
      <div className="grid grid-cols-[95px_1fr_75px] sm:grid-cols-[120px_1fr_90px] gap-2 mb-2">
        <FormInput
          label="CEP"
          value={address.cep}
          onChange={v => onCepLookup(v, setAddress, address)}
          placeholder="00000-000"
        />
        <FormInput
          label="Endereço"
          value={address.logradouro}
          onChange={v => setAddress({ ...address, logradouro: v, lat: undefined, lon: undefined })}
          placeholder="Rua / Avenida..."
        />
        <FormInput
          label="Número"
          value={address.numero}
          onChange={v => setAddress({ ...address, numero: v, lat: undefined, lon: undefined })}
          placeholder="Nº"
          id={numberInputId}
          isHighlighted={true}
          required={true}
        />
      </div>
      {/* Linha 2: Bairro | Cidade | Complemento */}
      <div className="grid grid-cols-3 gap-2">
        <FormInput
          label="Bairro"
          value={address.bairro}
          onChange={v => setAddress({ ...address, bairro: v })}
          placeholder="Centro"
        />
        <FormInput
          label="Cidade"
          value={address.cidade}
          onChange={v => setAddress({ ...address, cidade: v, lat: undefined, lon: undefined })}
          placeholder="BH / Betim / Contagem..."
        />
        <FormInput
          label="Complemento"
          value={address.complemento}
          onChange={v => setAddress({ ...address, complemento: v })}
          placeholder="Apto, Bloco..."
        />
      </div>
    </motion.div>
  );
}

/* ─── Inline Toast ─── */
function ValidationToast({ message, onClose }: { message: string; onClose: () => void }) {
  useEffect(() => {
    const t = setTimeout(onClose, 6000);
    return () => clearTimeout(t);
  }, [onClose]);

  return (
    <motion.div
      initial={{ opacity: 0, y: -30, scale: 0.95 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -30, scale: 0.95 }}
      className="fixed top-6 left-1/2 -translate-x-1/2 z-[9999] max-w-lg w-[90vw]"
    >
      <div className="bg-white border border-amber-200 shadow-2xl rounded-2xl p-4 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
          <AlertTriangle size={20} className="text-amber-600" />
        </div>
        <div className="flex-1">
          <p className="text-sm font-bold text-slate-800 mb-0.5">Atenção</p>
          <p className="text-sm text-slate-600 leading-relaxed">{message}</p>
        </div>
        <button
          onClick={onClose}
          className="text-slate-400 hover:text-slate-600 transition-colors text-lg font-bold leading-none mt-0.5"
          aria-label="Fechar"
        >
          ×
        </button>
      </div>
    </motion.div>
  );
}

/* ─── Main Quote Component ─── */
export function Quote() {
  const { config, isSurgeActive } = useAdmin();

  /* Steps & Form State */
  const [step, setStep] = useState(1);
  const [name, setName] = useState('');
  const [cpfCnpj, setCpfCnpj] = useState('');

  const [pickup, setPickup] = useState<Address>({ ...emptyAddress });
  const [stops, setStops] = useState<Address[]>([]);
  const [delivery, setDelivery] = useState<Address>({ ...emptyAddress });
  const [returnToPickup, setReturnToPickup] = useState(false);

  const [itemDescription, setItemDescription] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [recipientPhone, setRecipientPhone] = useState('');

  const [totalDistance, setTotalDistance] = useState(0);
  const [oneWayDistance, setOneWayDistance] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [isCalculating, setIsCalculating] = useState(false);
  const [toastMsg, setToastMsg] = useState<string | null>(null);

  /* ─── CEP Lookup ─── */
  const handleCepLookup = useCallback(async (cep: string, setter: (a: Address) => void, currentAddr: Address) => {
    setter({ ...currentAddr, cep });
    if (cep.replace(/\D/g, '').length === 8) {
      const data = await fetchAddressByCep(cep);
      if (data) {
        setter({
          ...currentAddr,
          cep,
          logradouro: data.logradouro || '',
          bairro: data.bairro || '',
          cidade: data.localidade || '',
          lat: undefined, lon: undefined
        });
      }
    }
  }, []);

  /* ─── Distance Calculation (debounced) ─── */
  useEffect(() => {
    const calculateRoute = async () => {
      const points = [pickup, ...stops, delivery];

      const validPoints = points.filter(p => p.logradouro && p.cidade && p.numero);
      if (validPoints.length < 2) { 
        setTotalDistance(0); 
        setOneWayDistance(0);
        return; 
      }

      setIsCalculating(true);

      const geocoded: { lat: number; lon: number }[] = [];
      for (const pt of validPoints) {
        if (pt.lat && pt.lon) {
          geocoded.push({ lat: pt.lat, lon: pt.lon });
        } else {
          const coords = await geocodeAddress(`${pt.logradouro}, ${pt.numero}, ${pt.bairro}, ${pt.cidade}`);
          if (coords) geocoded.push(coords);
        }
      }

      let oneWayDist = 0;
      if (geocoded.length >= 2) {
        for (let i = 0; i < geocoded.length - 1; i++) {
          oneWayDist += calculateDistance(geocoded[i].lat, geocoded[i].lon, geocoded[i + 1].lat, geocoded[i + 1].lon);
        }
      }
      oneWayDist *= 1.35;

      let returnDist = 0;
      if (returnToPickup && geocoded.length >= 2) {
        const first = geocoded[0];
        const last = geocoded[geocoded.length - 1];
        returnDist = calculateDistance(last.lat, last.lon, first.lat, first.lon) * 1.35;
      }

      setOneWayDistance(oneWayDist);
      setTotalDistance(oneWayDist + returnDist);
      setIsCalculating(false);
    };

    const t = setTimeout(calculateRoute, 1200);
    return () => clearTimeout(t);
  }, [pickup, stops, delivery, returnToPickup]);

  /* ─── Price Calculation ─── */
  const kmRate = isSurgeActive ? config.pricePerKm * config.surgeMultiplier : config.pricePerKm;

  const priceBreakdown = useMemo(() => {
    const subtotal = config.baseFare
      + oneWayDistance * kmRate
      + stops.length * config.extraStopFee
      + (returnToPickup ? config.returnFee : 0);

    const isLongDistance = oneWayDistance > config.longDistanceThresholdKm;
    const longDistanceSurcharge = isLongDistance
      ? subtotal * (config.longDistanceSurchargePercent / 100)
      : 0;

    return {
      subtotal,
      isLongDistance,
      longDistanceSurcharge,
      total: subtotal + longDistanceSurcharge,
    };
  }, [oneWayDistance, stops.length, returnToPickup, config, kmRate]);

  useEffect(() => {
    setTotalPrice(priceBreakdown.total);
  }, [priceBreakdown.total]);

  const nextStep = () => {
    // Validação rigorosa na etapa de contato (step 1)
    if (step === 1) {
      if (!name || name.trim() === '') {
        setToastMsg('Por favor, informe seu nome para começar.');
        return;
      }
      if (!cpfCnpj || !validarCpfCnpj(cpfCnpj)) {
        setToastMsg('Por favor, informe um CPF ou CNPJ válido para começar.');
        return;
      }
    }
    // Validação rigorosa na etapa de rota (step 2)
    if (step === 2) {
      const missing: { label: string; inputId: string }[] = [];

      if (!pickup.numero || pickup.numero.trim() === '') {
        missing.push({ label: 'Ponto de Coleta', inputId: 'input-numero-coleta' });
      }
      stops.forEach((s, i) => {
        if (!s.numero || s.numero.trim() === '') {
          missing.push({ label: `Ponto de Parada ${i + 1}`, inputId: `input-numero-parada-${i}` });
        }
      });
      if (!delivery.numero || delivery.numero.trim() === '') {
        missing.push({ label: 'Ponto Final', inputId: 'input-numero-entrega' });
      }

      if (missing.length > 0) {
        // Mensagem amigável
        let msg = '';
        if (missing.length === 1) {
          const p = missing[0].label;
          if (p === 'Ponto de Coleta') {
            msg = 'Ops! Quase lá. Por favor, informe o número do endereço no Ponto de Coleta para que o motorista saiba exatamente onde retirar.';
          } else if (p === 'Ponto Final') {
            msg = 'Falta pouco! Precisamos do número do endereço no Ponto Final para garantir a entrega correta.';
          } else {
            msg = `Esqueceu algo? Adicione o número do endereço no ${p} antes de prosseguir.`;
          }
        } else {
          const nomes = missing.map(m => m.label).join(' e ');
          msg = `Quase pronto! Por favor, preencha o número do endereço nos pontos indicados (${nomes}) para continuar.`;
        }

        // Exibe toast
        setToastMsg(msg);

        // Foco + scroll no primeiro campo vazio
        const firstEl = document.getElementById(missing[0].inputId) as HTMLInputElement | null;
        if (firstEl) {
          firstEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
          setTimeout(() => firstEl.focus(), 400);
        }
        return;
      }
    }
    // Validação rigorosa na etapa de descrição do item (step 3)
    if (step === 3) {
      if (!itemDescription || itemDescription.trim() === '') {
        setToastMsg('Por favor, preencha a descrição do item.');
        return;
      }
    }
    setStep(s => Math.min(s + 1, 4));
  };
  const prevStep = () => setStep(s => Math.max(s - 1, 1));

  const handleConfirmOrder = () => {
    if (!name || !pickup.logradouro || !delivery.logradouro) {
      alert("Por favor, preencha as informações obrigatórias antes de confirmar.");
      return;
    }
    if (!recipientName || recipientName.trim() === '') {
      setToastMsg("Por favor, informe o nome do recebedor antes de confirmar.");
      return;
    }

    const formatAddress = (a: Address) => `${a.logradouro}, ${a.numero}${a.complemento ? ' (' + a.complemento + ')' : ''} - ${a.bairro}, ${a.cidade}`;
    const now = new Date();
    const dataStr = now.toLocaleDateString('pt-BR');
    const horaStr = now.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

    let routeCounter = 1;

    let text = `Olá equipe ABH! Gostaria de solicitar um motoboy.\n`;
    text += `*Data do pedido:* ${dataStr} às ${horaStr}\n\n`;
    text += `*NOVA COTAÇÃO - ABH MOTOBOYS*\n`;
    text += `*Cliente:* ${name}\n`;
    text += `*CPF/CNPJ:* ${cpfCnpj}\n\n`;
    text += `*ROTA DA ENTREGA:*\n`;
    
    text += `${routeCounter}. *Coleta:* ${formatAddress(pickup)}\n`;
    routeCounter++;

    stops.forEach((s, i) => {
      if (s.logradouro) {
        text += `${routeCounter}. *Parada ${i + 1}:* ${formatAddress(s)}\n`;
        routeCounter++;
      }
    });

    text += `${routeCounter}. *Entrega:* ${formatAddress(delivery)}\n`;
    
    if (returnToPickup) text += `*Retorno à Coleta:* Sim\n`;
    if (priceBreakdown.isLongDistance) {
      text += `*Taxa de Retorno Automática:* +${config.longDistanceSurchargePercent}% (ida > ${config.longDistanceThresholdKm} km)\n`;
    }

    text += `\n*Detalhes do Item:* ${itemDescription || 'Não informado'}\n`;
    text += `*Recebedor:* ${recipientName || 'Não informado'} ${recipientPhone ? '(' + recipientPhone + ')' : ''}\n\n`;
    text += `*Distância:* ${totalDistance.toFixed(1)} km`;
    if (returnToPickup) {
      text += ` (Ida: ${oneWayDistance.toFixed(1)} km, Volta: ${(totalDistance - oneWayDistance).toFixed(1)} km)`;
    }
    text += `\n*Tempo Estimado:* ${Math.ceil(totalDistance * 2.5)} min\n`;
    text += `*Valor Estimado:* R$ ${totalPrice.toFixed(2)}\n\n`;
    text += `_*Aviso:* Esta cotação é uma estimativa. A confirmação e o valor final dependerão da aprovação da central._`;

    const waLink = `https://wa.me/5531986946035?text=${encodeURIComponent(text)}`;
    window.open(waLink, '_blank');
  };

  /* ─── Render ─── */
  return (
    <div className="flex flex-col lg:flex-row gap-8 items-start">

      {/* ═══════ LEFT: Form ═══════ */}
      <div className="flex-1 w-full max-w-3xl">

        {/* Hero Banner */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-5 bg-gradient-to-r from-brand-navy via-brand-blue to-brand-electric rounded-2xl p-5 text-white relative overflow-hidden"
        >
          <div className="absolute -right-8 -top-8 w-40 h-40 bg-white/5 rounded-full blur-2xl" />
          <div className="absolute right-12 bottom-0 w-24 h-24 bg-brand-green/15 rounded-full blur-xl" />
          <div className="relative z-10">
            <div className="flex items-center gap-2 text-brand-green text-xs font-bold uppercase tracking-widest mb-1.5">
              <Bike size={14} /> ABH Motoboys Expresso
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight mb-1">
              Cotação <span className="text-brand-green">Instantânea</span>
            </h2>
            <p className="text-white/70 max-w-md text-sm">
              Preencha os dados abaixo e receba o valor estimado da sua entrega em tempo real.
            </p>
          </div>
        </motion.div>

        {/* Step Indicator */}
        <div className="mb-4">
          <div className="flex items-center gap-1">
            {stepLabels.map((label, i) => {
              const n = i + 1;
              const isActive = step === n;
              const isDone = step > n;
              return (
                <div key={n} className="flex-1 flex flex-col items-center gap-1">
                  <div className="w-full flex items-center">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold transition-all duration-300 shrink-0 ${
                        isDone
                          ? 'bg-brand-green text-white shadow-lg shadow-brand-green/30'
                          : isActive
                          ? 'bg-brand-blue text-white shadow-lg shadow-brand-blue/30 scale-110'
                          : 'bg-slate-200 text-slate-400'
                      }`}
                    >
                      {isDone ? <CheckCircle size={12} /> : n}
                    </div>
                    {i < 3 && (
                      <div className={`flex-1 h-0.5 mx-1 rounded-full transition-colors duration-300 ${
                        isDone ? 'bg-brand-green' : 'bg-slate-200'
                      }`} />
                    )}
                  </div>
                  <span className={`text-[10px] font-semibold uppercase tracking-wider transition-colors ${
                    isActive ? 'text-brand-blue' : isDone ? 'text-brand-green' : 'text-slate-400'
                  }`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Form Card */}
        <div className="glassmorphism rounded-2xl p-5 sm:p-6 relative overflow-hidden min-h-[320px]">
          <AnimatePresence mode="wait">

            {/* ── Step 1: Contact ── */}
            {step === 1 && (
              <motion.div key="s1" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <h2 className="text-lg font-extrabold text-brand-navy mb-1 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-blue to-brand-electric flex items-center justify-center text-white">
                    <User size={16} />
                  </div>
                  Suas Informações
                </h2>
                <p className="text-slate-500 text-xs mb-6 ml-10">Precisamos do seu nome e documento para começar.</p>

                <div className="max-w-md">
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    Nome Completo <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    className="w-full rounded-xl border border-slate-200/80 bg-white/70 p-3 text-base text-slate-800 placeholder:text-slate-400 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 outline-none transition-all duration-200 mb-4"
                    placeholder="Como devemos chamá-lo?"
                    autoFocus
                  />

                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1.5">
                    CPF / CNPJ <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={cpfCnpj}
                    onChange={e => setCpfCnpj(mascaraCpfCnpj(e.target.value))}
                    className="w-full rounded-xl border border-slate-200/80 bg-white/70 p-3 text-base text-slate-800 placeholder:text-slate-400 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 outline-none transition-all duration-200"
                    placeholder="000.000.000-00 ou 00.000.000/0001-00"
                  />
                </div>
              </motion.div>
            )}

            {/* ── Step 2: Route ── */}
            {step === 2 && (
              <motion.div key="s2" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <h2 className="text-lg font-extrabold text-brand-navy mb-1 flex items-center gap-2">
                  <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-brand-green to-emerald-700 flex items-center justify-center text-white">
                    <MapPin size={16} />
                  </div>
                  Rota da Entrega
                </h2>
                <p className="text-slate-500 text-xs mb-4 ml-10">Informe o endereço de coleta e entrega. O CEP preenche automaticamente.</p>

                <AddressBlock
                  title="Ponto de Coleta"
                  icon={<div className="w-5 h-5 rounded-full bg-brand-green text-white flex items-center justify-center text-[10px] font-bold">A</div>}
                  address={pickup}
                  setAddress={setPickup}
                  onCepLookup={handleCepLookup}
                  numberInputId="input-numero-coleta"
                />

                {stops.map((stop, idx) => (
                  <div key={idx} className="relative">
                    <AddressBlock
                      title={`Parada ${idx + 1}`}
                      icon={<div className="w-5 h-5 rounded-full bg-amber-500 text-white flex items-center justify-center text-[10px] font-bold">{idx + 1}</div>}
                      address={stop}
                      setAddress={(val) => {
                        const ns = [...stops];
                        ns[idx] = val;
                        setStops(ns);
                      }}
                      onCepLookup={handleCepLookup}
                      numberInputId={`input-numero-parada-${idx}`}
                    />
                    <button
                      onClick={() => setStops(stops.filter((_, i) => i !== idx))}
                      className="absolute top-5 right-5 text-xs text-red-400 hover:text-red-600 font-semibold transition-colors"
                    >
                      Remover
                    </button>
                  </div>
                ))}

                {stops.length < 2 && (
                  <button
                    onClick={() => setStops([...stops, { ...emptyAddress }])}
                    className="mb-4 flex items-center gap-2 text-sm font-semibold text-brand-blue hover:text-brand-electric transition-colors py-2"
                  >
                    <PlusCircle size={16} /> Adicionar Parada Intermediária
                  </button>
                )}

                <AddressBlock
                  title="Ponto de Entrega (Final)"
                  icon={<div className="w-5 h-5 rounded-full bg-red-500 text-white flex items-center justify-center text-[10px] font-bold">B</div>}
                  address={delivery}
                  setAddress={setDelivery}
                  onCepLookup={handleCepLookup}
                  numberInputId="input-numero-entrega"
                />

                {/* Return Toggle */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="mt-4 flex items-center gap-3 bg-gradient-to-r from-slate-50 to-white p-4 rounded-xl border border-slate-200/80 cursor-pointer hover:border-brand-blue/30 transition-colors"
                  onClick={() => setReturnToPickup(!returnToPickup)}
                >
                  <div className={`w-12 h-6 rounded-full p-0.5 transition-colors duration-300 ${returnToPickup ? 'bg-brand-blue' : 'bg-slate-300'}`}>
                    <motion.div
                      animate={{ x: returnToPickup ? 24 : 0 }}
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                      className="w-5 h-5 bg-white rounded-full shadow-md"
                    />
                  </div>
                  <div>
                    <span className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
                      <RotateCcw size={14} className="text-brand-blue" />
                      Retornar ao Ponto de Coleta
                    </span>
                    <span className="text-xs text-slate-400">Adicional de R$ {config.returnFee.toFixed(2)}</span>
                  </div>
                </motion.div>
              </motion.div>
            )}

            {/* ── Step 3: Item Details ── */}
            {step === 3 && (
              <motion.div key="s3" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <h2 className="text-xl font-extrabold text-brand-navy mb-1 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white">
                    <Package size={18} />
                  </div>
                  Detalhes do Item
                </h2>
                <p className="text-slate-500 text-sm mb-6 ml-12">Descreva o que estamos transportando.</p>

                <div className="bg-amber-50/80 border border-amber-200/70 rounded-2xl p-4 mb-6 flex gap-3 text-amber-800 text-sm">
                  <AlertTriangle size={20} className="shrink-0 text-amber-500 mt-0.5" />
                  <div>
                    <p className="font-bold mb-1">Atenção — Restrições de Transporte</p>
                    <ul className="space-y-0.5 text-amber-700">
                      <li>• Peso máximo: <strong>15 KG</strong></li>
                      <li>• Não transportamos itens <strong>frágeis</strong> ou <strong>perecíveis</strong></li>
                      <li>• Itens de alto valor devem ser comunicados previamente à central</li>
                    </ul>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">
                    Descrição do Item <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={itemDescription}
                    onChange={e => setItemDescription(e.target.value)}
                    rows={4}
                    className="w-full rounded-2xl border border-slate-200/80 bg-white/60 p-4 text-sm text-slate-800 placeholder:text-slate-400 focus:border-brand-blue focus:ring-2 focus:ring-brand-blue/15 outline-none transition-all duration-200 resize-none"
                    placeholder="Ex: Documentos, caixa pequena com peças, envelope A4..."
                  />
                </div>
              </motion.div>
            )}

            {/* ── Step 4: Recipient ── */}
            {step === 4 && (
              <motion.div key="s4" variants={pageVariants} initial="enter" animate="center" exit="exit" transition={{ duration: 0.3 }}>
                <h2 className="text-xl font-extrabold text-brand-navy mb-1 flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-purple-500 to-violet-700 flex items-center justify-center text-white">
                    <CheckCircle size={18} />
                  </div>
                  Dados do Recebedor
                </h2>
                <p className="text-slate-500 text-sm mb-6 ml-12">Quem vai receber a entrega no destino?</p>

                <div className="space-y-4 max-w-lg">
                  <FormInput
                    label="Nome do Recebedor"
                    value={recipientName}
                    onChange={setRecipientName}
                    placeholder="Nome de quem vai receber"
                    required={true}
                  />
                  <FormInput
                    label="Telefone de Contato"
                    value={recipientPhone}
                    onChange={setRecipientPhone}
                    placeholder="(31) 99999-9999"
                    type="tel"
                  />
                </div>
              </motion.div>
            )}

          </AnimatePresence>

          {/* Toast de validação */}
          <AnimatePresence>
            {toastMsg && <ValidationToast message={toastMsg} onClose={() => setToastMsg(null)} />}
          </AnimatePresence>

          {/* Navigation */}
          <div className="mt-8 flex justify-between items-center pt-6 border-t border-slate-100/80">
            {step > 1 ? (
              <button
                onClick={prevStep}
                className="flex items-center gap-2 text-slate-400 font-semibold hover:text-brand-navy transition-colors px-4 py-2.5 rounded-xl hover:bg-slate-50"
              >
                <ArrowLeft size={18} /> Voltar
              </button>
            ) : <div />}

            {step < 4 ? (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={nextStep}
                className="flex items-center gap-2 bg-gradient-to-r from-brand-blue to-brand-electric text-white px-7 py-3 rounded-xl font-bold shadow-lg shadow-brand-blue/25 hover:shadow-xl hover:shadow-brand-blue/30 transition-shadow"
              >
                Próximo <ArrowRight size={18} />
              </motion.button>
            ) : (
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={handleConfirmOrder}
                className="flex items-center gap-2 bg-gradient-to-r from-brand-green to-emerald-500 text-white px-8 py-3.5 rounded-xl font-extrabold text-lg shadow-lg shadow-brand-green/30 hover:shadow-xl hover:shadow-brand-green/40 transition-shadow"
              >
                <Sparkles size={20} /> Confirmar Pedido
              </motion.button>
            )}
          </div>
        </div>
      </div>

      {/* ═══════ RIGHT: Live Quote Card ═══════ */}
      <div className="w-full lg:w-[340px] shrink-0 lg:sticky lg:top-4">
        <motion.div
          initial={{ opacity: 0, y: 30, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
          className="glassmorphism-dark rounded-2xl p-5 text-white relative overflow-hidden"
        >
          {/* Decorative blurs */}
          <div className="absolute -top-12 -right-12 w-36 h-36 bg-brand-blue/25 blur-3xl rounded-full pointer-events-none" />
          <div className="absolute -bottom-8 -left-8 w-28 h-28 bg-brand-green/15 blur-2xl rounded-full pointer-events-none" />

          <h3 className="text-lg font-extrabold mb-1 flex items-center gap-2.5 relative z-10">
            <Zap size={18} className="text-brand-green drop-shadow-[0_0_6px_rgba(34,197,94,0.8)]" />
            <span className="bg-gradient-to-r from-white to-brand-blue-ice bg-clip-text text-transparent">Cotação em Tempo Real</span>
          </h3>
          <p className="text-white/60 text-xs mb-6 relative z-10">Atualizada enquanto você preenche</p>

          <div className="space-y-3 mb-6 relative z-10">
            {recipientName && recipientName.trim() !== '' ? (
              <>
                <Row label="Tarifa Mínima" value={`R$ ${config.baseFare.toFixed(2)}`} />
                <Row label="Distância Total" value={`${totalDistance.toFixed(1)} km`} color="green" />
                {totalDistance > 0 && (
                  <Row label="Tempo Estimado" value={`~${Math.ceil(totalDistance * 2.5)} min`} color="blue" />
                )}

                {oneWayDistance > 0 && (
                  <Row
                    label={`KM cobrado (Ida) × R$ ${(isSurgeActive ? config.pricePerKm * config.surgeMultiplier : config.pricePerKm).toFixed(2)}`}
                    value={`R$ ${(oneWayDistance * (isSurgeActive ? config.pricePerKm * config.surgeMultiplier : config.pricePerKm)).toFixed(2)}`}
                    color="amber"
                  />
                )}

                {stops.length > 0 && (
                  <Row label={`Paradas Extras (${stops.length})`} value={`R$ ${(stops.length * config.extraStopFee).toFixed(2)}`} color="amber" />
                )}

                {returnToPickup && (
                  <Row label="Retorno à Coleta" value={`R$ ${config.returnFee.toFixed(2)}`} color="amber" />
                )}

                {priceBreakdown.isLongDistance && (
                  <Row
                    label={`Taxa de Retorno Automática (+${config.longDistanceSurchargePercent}%)`}
                    value={`R$ ${priceBreakdown.longDistanceSurcharge.toFixed(2)}`}
                    color="amber"
                  />
                )}
              </>
            ) : (
              <>
                <Row label="Distância Total" value={`${totalDistance.toFixed(1)} km`} color="green" />
                {totalDistance > 0 && (
                  <Row label="Tempo Estimado" value={`~${Math.ceil(totalDistance * 2.5)} min`} color="blue" />
                )}
                <div className="bg-white/5 border border-white/10 rounded-xl p-3 text-xs text-white/70 leading-relaxed">
                  Os valores da cotação serão revelados após o preenchimento do recebedor (Etapa 4).
                </div>
              </>
            )}

            {isSurgeActive && recipientName && recipientName.trim() !== '' && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                className="flex items-center gap-2 text-xs bg-brand-green/20 text-brand-green border border-brand-green/40 p-3 rounded-xl font-bold"
              >
                <Zap size={14} />
                Alta Demanda — {config.surgeMultiplier}x ativo
              </motion.div>
            )}
          </div>

          <div className="border-t border-white/15 pt-4 relative z-10">
            <div className="flex justify-between items-end">
              <span className="text-white/70 text-sm font-semibold">Total Estimado</span>
              <div className="text-right">
                {isCalculating ? (
                  <span className="text-xl font-bold text-white/50 animate-pulse">Calculando...</span>
                ) : !recipientName || recipientName.trim() === '' ? (
                  <span className="text-sm font-semibold text-amber-300 animate-pulse">Aguardando recebedor...</span>
                ) : (
                  <motion.span
                    key={totalPrice.toFixed(2)}
                    initial={{ scale: 0.8, opacity: 0, y: 8 }}
                    animate={{ scale: 1, opacity: 1, y: 0 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                    className="text-4xl font-black bg-gradient-to-r from-brand-green via-emerald-300 to-white bg-clip-text text-transparent drop-shadow-[0_0_12px_rgba(34,197,94,0.4)]"
                  >
                    R$ {totalPrice.toFixed(2)}
                  </motion.span>
                )}
              </div>
            </div>
          </div>

          <div className="mt-5 p-3.5 rounded-xl bg-white/5 border border-amber-500/30 text-slate-200 relative z-10 backdrop-blur-md shadow-lg shadow-black/20">
            <div className="flex gap-2.5 items-start">
              <AlertTriangle className="w-4 h-4 text-amber-400 shrink-0 mt-0.5 animate-pulse" />
              <div className="text-[11px] leading-relaxed text-left">
                <span className="text-amber-400 font-black tracking-wider uppercase mr-1">Atenção:</span>
                O valor final pode mudar se o serviço for complexo, ou se houver demora no atendimento ao motoboy (seja na coleta ou na entrega).
                <div className="mt-2 text-brand-green font-black flex items-center gap-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-brand-green animate-ping shrink-0" />
                  Caso aconteça, você será avisado pela central Abhmotoboys!
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Quick info card */}
        <div className="mt-4 bg-white/50 border border-slate-100 rounded-2xl p-4 text-center">
          <p className="text-xs text-slate-500">
            <Phone size={12} className="inline mr-1 -mt-0.5" />
            Dúvidas? <a href="https://wa.me/5531986946035" target="_blank" rel="noopener noreferrer" className="text-brand-green font-bold hover:underline">Fale pelo WhatsApp</a>
          </p>
        </div>
      </div>
    </div>
  );
}

/* ─── Price Row ─── */
function Row({ label, value, color }: { label: string; value: string; color?: 'green' | 'blue' | 'amber' }) {
  const valueClass =
    color === 'green'  ? 'text-brand-green font-bold drop-shadow-[0_0_6px_rgba(34,197,94,0.5)]' :
    color === 'blue'   ? 'text-brand-blue-ice font-bold' :
    color === 'amber'  ? 'text-amber-300 font-bold' :
    'text-white font-semibold';

  return (
    <div className="flex justify-between items-center text-sm py-0.5">
      <span className="text-white/70">{label}</span>
      <span className={valueClass}>{value}</span>
    </div>
  );
}
